import {
  ALL_SHOT_RESULTS,
  ALL_SHOT_TYPES,
  ALL_WEIGHTS,
  Game,
  PlayerStats,
  ShotResult,
  ShotType,
  TeamStats,
  Weight,
} from '../types/curling';
import { computePlayerScores, computeTeamScore } from './playerScoring';
import { calculateTeamEndOutcomeRates, calculateTeamPointsStats } from './teamPointsStats';

/**
 * Client-side aggregates. The same figures are also available from the API
 * (/api/analytics/...), which is preferable once a page no longer needs the full
 * game list in memory for other reasons.
 */

const POINTS_BY_RESULT: Record<ShotResult, number> = {
  excellent: 4,
  good: 3,
  fair: 2,
  poor: 1,
  miss: 0,
};

export const getPointsFromResult = (result: ShotResult): number => POINTS_BY_RESULT[result];

/**
 * Builds a zero-filled tally keyed by every member of `keys`.
 *
 * Previously these tallies were hand-written object literals, and the shot-type one
 * omitted 'peel'. A single peel shot then incremented `undefined`, which turned the
 * entire tally into NaN. Deriving the keys from the type's own list keeps that from
 * recurring as shot types are added.
 */
const zeroTally = <K extends string>(keys: readonly K[]): Record<K, number> =>
  Object.fromEntries(keys.map(key => [key, 0])) as Record<K, number>;

export const calculatePlayerStats = (games: Game[], playerName: string): PlayerStats => {
  const allShots = games.flatMap(game =>
    game.ends.flatMap(end => end.shots.filter(shot => shot.playerName === playerName))
  );

  const shotTypes = zeroTally<ShotType>(ALL_SHOT_TYPES);
  const results = zeroTally<ShotResult>(ALL_SHOT_RESULTS);
  const averageWeight = zeroTally<Weight>(ALL_WEIGHTS);

  const weightByResult = Object.fromEntries(
    ALL_WEIGHTS.map(weight => [weight, zeroTally<ShotResult>(ALL_SHOT_RESULTS)])
  ) as PlayerStats['weightByResult'];

  let totalCurl = 0;
  let totalTime = 0;
  let validTimeCount = 0;

  allShots.forEach(shot => {
    shotTypes[shot.shotType]++;
    results[shot.result]++;
    averageWeight[shot.weight]++;
    weightByResult[shot.weight][shot.result]++;
    totalCurl += shot.curl;
    // Only include times > 0 (ignore 0.0s times)
    if (shot.time > 0) {
      totalTime += shot.time;
      validTimeCount++;
    }
  });

  const successRate =
    allShots.length > 0 ? ((results.excellent + results.good) / allShots.length) * 100 : 0;

  return {
    playerName,
    totalShots: allShots.length,
    shotTypes,
    results,
    successRate,
    averageWeight,
    averageCurl: allShots.length > 0 ? totalCurl / allShots.length : 0,
    averageTime: validTimeCount > 0 ? totalTime / validTimeCount : 0,
    weightByResult,
  };
};

export const calculateTeamStats = (games: Game[], teamName: string): TeamStats => {
  const teamGames = games.filter(
    game => game.team1Name === teamName || game.team2Name === teamName
  );

  const completedGames = teamGames.filter(game => game.isComplete);

  const gamesWon = completedGames.filter(game => {
    const isTeam1 = game.team1Name === teamName;
    return isTeam1
      ? game.team1TotalScore > game.team2TotalScore
      : game.team2TotalScore > game.team1TotalScore;
  }).length;

  const gamesLost = completedGames.filter(game => {
    const isTeam1 = game.team1Name === teamName;
    return isTeam1
      ? game.team1TotalScore < game.team2TotalScore
      : game.team2TotalScore < game.team1TotalScore;
  }).length;

  const totalPoints = teamGames.reduce((sum, game) => {
    const isTeam1 = game.team1Name === teamName;
    return sum + (isTeam1 ? game.team1TotalScore : game.team2TotalScore);
  }, 0);

  const totalPointsAgainst = teamGames.reduce((sum, game) => {
    const isTeam1 = game.team1Name === teamName;
    return sum + (isTeam1 ? game.team2TotalScore : game.team1TotalScore);
  }, 0);

  const allShots = teamGames.flatMap(game =>
    game.ends.flatMap(end =>
      end.shots.filter(shot => {
        const isTeam1 = game.team1Name === teamName;
        return isTeam1 ? shot.team === 'team1' : shot.team === 'team2';
      })
    )
  );

  const traditionalScore = allShots.length > 0
    ? (allShots.reduce((sum, shot) => sum + getPointsFromResult(shot.result), 0) / (4 * allShots.length)) * 100
    : 0;

  const goldStars = allShots.filter(shot => shot.isGoldStar).length;
  const bombs = allShots.filter(shot => shot.isBomb).length;
  const heavyShots = allShots.filter(shot => shot.weight === 'heavy').length;
  const lightShots = allShots.filter(shot => shot.weight === 'light').length;

  // Same "mean of every player-game's overallScore" recipe a single game's team score
  // uses (see computeTeamScore), just fed every game the team has played instead of one.
  const teamPlayerScores = teamGames.flatMap(game => {
    const side = game.team1Name === teamName ? 'team1' : 'team2';
    return computePlayerScores(game).filter(p => p.team === side);
  });
  const teamExecutionScore = computeTeamScore(teamPlayerScores).overallScore;

  const pointsStats = calculateTeamPointsStats(teamGames, teamName);
  const endOutcomeRates = calculateTeamEndOutcomeRates(teamGames, teamName);

  return {
    teamName,
    gamesPlayed: completedGames.length,
    gamesWon,
    gamesLost,
    totalPoints,
    totalPointsAgainst,
    totalShots: allShots.length,
    averagePointsPerGame: completedGames.length > 0 ? totalPoints / completedGames.length : 0,
    traditionalScore,
    teamExecutionScore,
    goldStars,
    bombs,
    heavyPercent: allShots.length > 0 ? (heavyShots / allShots.length) * 100 : 0,
    lightPercent: allShots.length > 0 ? (lightShots / allShots.length) * 100 : 0,
    totalStolenPointsFor: pointsStats.stolenPointsFor,
    totalStolenPointsGiven: pointsStats.stolenPointsGiven,
    endsPlayed: pointsStats.endsPlayed,
    pfa: pointsStats.pfa,
    pga: pointsStats.pga,
    spfa: pointsStats.spfa,
    spga: pointsStats.spga,
    hammerConversionRate: endOutcomeRates.hammerConversionRate,
    forcePercent: endOutcomeRates.forcePercent,
    stealRate: endOutcomeRates.stealRate,
    blankRate: endOutcomeRates.blankRate,
    bigEndRateFor: endOutcomeRates.bigEndRateFor,
    bigEndRateAgainst: endOutcomeRates.bigEndRateAgainst,
  };
};

export const getAllPlayers = (games: Game[]): string[] => {
  const players = new Set<string>();
  games.forEach(game => {
    game.ends.forEach(end => {
      end.shots.forEach(shot => {
        players.add(shot.playerName);
      });
    });
  });
  return Array.from(players);
};

export const getAllTeams = (games: Game[]): string[] => {
  const teams = new Set<string>();
  games.forEach(game => {
    teams.add(game.team1Name);
    teams.add(game.team2Name);
  });
  return Array.from(teams);
};

export const getAllCurlingClubs = (games: Game[]): string[] => {
  const clubs = new Set<string>();
  games.forEach(game => {
    if (game.curlingClub) {
      clubs.add(game.curlingClub);
    }
  });
  return Array.from(clubs).sort();
};

export const getSheetsForClub = (games: Game[], clubName: string): string[] => {
  const sheets = new Set<string>();
  games.forEach(game => {
    if (game.curlingClub === clubName && game.sheetNumber) {
      sheets.add(game.sheetNumber);
    }
  });
  return Array.from(sheets).sort((a, b) => {
    // Try to sort numerically if possible
    const numA = parseInt(a);
    const numB = parseInt(b);
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }
    return a.localeCompare(b);
  });
};
